import React, { useState } from 'react';
import { appAgentService } from '../../../services/central-api/app-agent';
import Toast from 'react-native-toast-message';
import { ScrollView } from 'react-native-web-hover';
import MyAppText from '../../../components/MyAppText';
import { Modal, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import InvolvedVehicles from '../../../components/app-agent/InvolvedVehicles';
import InvolvedPeople from '../../../components/app-agent/InvolvedPeople';
import InvolvedObjects from '../../../components/app-agent/InvolvedObjects';
import { ClientError } from '../../../services/central-api/base-service';
import { RecordNotLatestValues } from '../../../services/central-api/dispatch';
import ConflictUpdateModal from '../../../components/ConflictUpdateModal';
import { RootNavigation } from '../../../Routes';

interface AgentOccurrenceInvolvedParams {
    occurrence: Occurrence;
    involvedVehicles: InvolvedVehicle[];
    setInvolvedVehicles: React.Dispatch<React.SetStateAction<InvolvedVehicle[]>>;
    involvedPeople: InvolvedPerson[];
    setInvolvedPeople: React.Dispatch<React.SetStateAction<InvolvedPerson[]>>;
    involvedObjects: InvolvedObject[];
    setInvolvedObjects: React.Dispatch<React.SetStateAction<InvolvedObject[]>>;
    shouldFinishOccurrence: boolean;
    navigation: RootNavigation;
    setStep: React.Dispatch<React.SetStateAction<'occurrenceDetail' | 'occurrenceReport' | 'occurrenceInvolved' | 'displaceQuestionary' | 'policeReport'>>;
    getOccurrence: (occurrenceId: number) => Promise<void>;
}

export default function AgentOccurrenceInvolved({
    occurrence,
    involvedObjects,
    setInvolvedObjects,
    involvedPeople,
    setInvolvedPeople,
    involvedVehicles,
    setInvolvedVehicles,
    shouldFinishOccurrence,
    navigation,
    setStep,
    getOccurrence
}: AgentOccurrenceInvolvedParams) {
    const { styles, theme } = useStyles(styleSheet);

    const [conflictModalVisible, setConflictModalVisible] = useState(false);
    const [conflictUpdateUser, setConflictUpdateUser] = useState<string>('');
    const [isScreenVisible, setIsScreenVisible] = useState<boolean>(true);

    const [originalInvolvedVehicles, setOriginalInvolvedVehicles] = useState<InvolvedVehicle[]>(involvedVehicles);
    const [originalInvolvedObjects, setOriginalInvolvedObjects] = useState<InvolvedObject[]>(involvedObjects);
    const [originalInvolvedPeople, setOriginalInvolvedPeople] = useState<InvolvedPerson[]>(involvedPeople);

    async function updateOccurrenceInvolved() {
        if (!occurrence?.id) {
            return;
        }

        try {
            await appAgentService.updateOccurrence({
                id: occurrence.id,
                involvedVehicles: involvedVehicles.map(involvedVehicle => involvedVehicle.id && involvedVehicle.id < 0 ? { ...involvedVehicle, id: undefined } : involvedVehicle),
                involvedPeople: involvedPeople.map(involvedPerson => involvedPerson.id && involvedPerson.id < 0 ? { ...involvedPerson, id: undefined } : involvedPerson),
                involvedObjects: involvedObjects.map(involvedObject => involvedObject.id && involvedObject.id < 0 ? { ...involvedObject, id: undefined } : involvedObject),
                updatedAt: occurrence.updatedAt
            });

            await getOccurrence(occurrence.id);

            setOriginalInvolvedVehicles(involvedVehicles);
            setOriginalInvolvedObjects(involvedObjects);
            setOriginalInvolvedPeople(involvedPeople);

            if (shouldFinishOccurrence) {
                setStep('occurrenceDetail');
            }

        } catch (err) {
            console.error(err);
            if (err instanceof RecordNotLatestValues) {
                setConflictModalVisible(true);
                setConflictUpdateUser(err.userName);
                return;
            } else if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <>
            <Modal
                visible={isScreenVisible}
                onRequestClose={() => {
                    setIsScreenVisible(false);
                }}
                transparent={true}
            >
                <ScrollView contentContainerStyle={{ marginTop: 64, flex: 1, backgroundColor: theme.colors.paginationBar.background }}>
                    <View style={styles.container} >
                        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                            <TouchableOpacity style={{}} onPress={async () => {
                                setIsScreenVisible(false);
                                setStep('occurrenceDetail');
                            }}>
                                <FontAwesomeIcon fontSize={30} icon={faArrowLeft} color={theme.colors.iconColor} />
                            </TouchableOpacity>
                            <View style={{ flex: 1, alignItems: 'center' }}>
                                <MyAppText style={{ color: theme.colors.labelColor, fontSize: 16 }}>{translate('involved').toUpperCase()} </MyAppText>
                            </View>
                        </View>
                        <View>
                            <InvolvedVehicles
                                involvedVehicles={involvedVehicles}
                                setInvolvedVehicles={setInvolvedVehicles}
                                updateOccurrenceInvolved={updateOccurrenceInvolved}
                            />
                        </View>
                        <View>
                            <InvolvedPeople
                                involvedPeople={involvedPeople}
                                setInvolvedPeople={setInvolvedPeople}
                                updateOccurrenceInvolved={updateOccurrenceInvolved}
                            />
                        </View>
                        <View>
                            <InvolvedObjects
                                involvedObjects={involvedObjects}
                                setInvolvedObjects={setInvolvedObjects}
                                updateOccurrenceInvolved={updateOccurrenceInvolved}
                            />
                        </View>
                    </View>
                </ScrollView>
            </Modal>
            <ConflictUpdateModal
                setModalVisible={setConflictModalVisible}
                isModalVisible={conflictModalVisible}
                onSubmit={() => {
                    if (occurrence.id) {
                        navigation.navigate('AppAgent', { screen: 'AgentOccurrenceDetails', params: { occurrenceId: occurrence.id } });
                    }
                }}
                userName={conflictUpdateUser}
                onCancel={() => {
                    setConflictUpdateUser('');
                    setInvolvedObjects(originalInvolvedObjects);
                    setInvolvedPeople(originalInvolvedPeople);
                    setInvolvedVehicles(originalInvolvedVehicles);
                }}
            />
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        padding: 16,
        gap: 20
    },
    loading: {
        marginHorizontal: 'auto',
        transform: [{ scaleX: 2 }, { scaleY: 2 }],
        width: '20%',
        height: '20%',
    },
    loadingText: {
        color: theme.colors.loadingColor,
        textAlign: 'center',
        fontSize: 16
    },
    loadingView: {
        flex: 1,
        justifyContent: 'center',
        alignContent: 'center'
    },
}));
